import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";
import { getPostImageUrl } from "../lib/blogImages";
import { useLanguage } from "../context/LanguageContext";

const formatDate = (dateString, locale) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const BlogCard = ({ post }) => {
  const { language, t } = useLanguage();
  const locale = language === "uk" ? "uk-UA" : "en-CA";
  const imageUrl = getPostImageUrl(post);
  const imageAlt = post.mainImage?.alt || post.title;

  return (
    <motion.article
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/80 bg-white/75 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-md transition-all duration-500 hover:border-yellow-300/60 hover:shadow-[0_20px_50px_rgba(161,98,7,0.12)]"
      whileHover={{ y: -6 }}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-yellow-300 via-yellow-500 to-amber-400 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <Link to={`/blog/${post.slug}`} className="flex h-full flex-col">
        <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-stone-100 to-amber-50">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-stone-900/10 to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-80" />
          <div className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/90 px-3 py-1 text-xs font-medium text-yellow-800 shadow-sm backdrop-blur-sm">
            <Calendar size={12} className="text-yellow-600" />
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, locale)}</time>
          </div>
        </div>

        <div className="flex flex-grow flex-col p-6 md:p-7">
          <h3 className="mb-3 line-clamp-2 text-xl font-bold leading-snug text-gray-900 transition-colors duration-300 group-hover:text-yellow-700">
            {post.title}
          </h3>

          {post.excerpt && (
            <p className="line-clamp-3 flex-grow leading-relaxed text-gray-600">{post.excerpt}</p>
          )}

          <div className="mt-6 flex items-center justify-between border-t border-yellow-100/80 pt-5">
            <span className="text-sm font-semibold text-yellow-700 transition-colors group-hover:text-yellow-800">
              {t("blog.readMore")}
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-50 text-yellow-600 transition-all duration-300 group-hover:bg-yellow-500 group-hover:text-white">
              <ArrowUpRight size={16} />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

export default BlogCard;
